import { GENERIC_SERVER_ERROR_TRANSLATION } from 'core/constants/constants';
import { useDeviceTypes } from 'core/contexts/device-types/hooks/use-device-types.hook';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { usePocQuery } from 'core/custom-hooks/use-poc-query.hook';
import { useTranslation } from 'core/i18n/i18n';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { cancelQuery, setQueryCache } from 'core/query-client/query-client';
import { executeRequest } from 'core/utils/backend-service';
import { showErrorNotification } from 'core/utils/notification-service';
import { useParams } from 'react-router-dom';
import {
    getGeneralDeviceSettingsConfiguration,
    updateGeneralDeviceConfiguration,
} from '../../../service/settings-devices-configuration.service';
import { STATUS_TRACKING_ERRORS_MAPPINGS } from '../../constants';
import {
    CleaningProperties,
    ConnectivityProperties,
    DeviceSettingsGeneralConfiguration,
    QcProperties,
} from '../../settings-device-configuration-status-tracking.model';
import { useConfigurationStatusTrackingFormValidation } from './use-configuration-status-tracking-form-validation.hook';
import { DeviceSettingsGeneralConfigurationFormValidationKeys } from './configuration-status-tracking.validation.type';

export const useSettingsDeviceConfigurationStatusTracking = () => {
    const { areOtherSectionsValid, handleValidityChange } = useConfigurationStatusTrackingFormValidation();

    const { id } = useParams();
    const { getDeviceTypeDetailsBySlug } = useDeviceTypes();
    const { t } = useTranslation();
    const deviceType = getDeviceTypeDetailsBySlug(id ?? '');
    const statusTrackingQueryKey = settingsKeys.configDetail(`status-tracking-${deviceType?.id}`);

    const { data: config, isLoading } = usePocQuery<DeviceSettingsGeneralConfiguration>({
        queryKey: statusTrackingQueryKey,
        queryFn: () => executeRequest(() => getGeneralDeviceSettingsConfiguration(deviceType!.id)),
        enabled: !!deviceType,
    });

    const { mutateAsync: updateGeneralConfiguration } = usePocMutation({
        mutationFn: (next: DeviceSettingsGeneralConfiguration) =>
            executeRequest(() => updateGeneralDeviceConfiguration(deviceType!.id, next)),
        onError: (error: unknown) => {
            let translationKey = GENERIC_SERVER_ERROR_TRANSLATION;

            try {
                const parsedError = JSON.parse((error as Error).message);
                const errorCode = parsedError.errors?.[0]?.extensions?.code;
                const mapping = STATUS_TRACKING_ERRORS_MAPPINGS.find((m) => m.errorCode === errorCode);

                if (mapping) {
                    translationKey = mapping.translationKey;
                }
            } catch {
                // If parsing fails, the default generic translation is kept
            }

            showErrorNotification(t(translationKey));
        },
    });

    const updateConfiguration = (
        changedSection: DeviceSettingsGeneralConfigurationFormValidationKeys,
        next: DeviceSettingsGeneralConfiguration
    ) => {
        if (deviceType?.id && areOtherSectionsValid(changedSection)) {
            // mutateAsync rejects on failure; onError already notifies, so swallow to avoid an unhandled rejection.
            updateGeneralConfiguration(next).catch(() => undefined);
        }
    };

    const applyEditToCachedConfig = <K extends DeviceSettingsGeneralConfigurationFormValidationKeys>(
        key: K,
        values: DeviceSettingsGeneralConfiguration[K]
    ) => {
        void cancelQuery(statusTrackingQueryKey);

        return setQueryCache<DeviceSettingsGeneralConfiguration>(
            statusTrackingQueryKey,
            (current) => current && { ...current, [key]: values }
        );
    };

    const handleSectionChange = <K extends DeviceSettingsGeneralConfigurationFormValidationKeys>(
        key: K,
        values: DeviceSettingsGeneralConfiguration[K]
    ) => {
        const next = applyEditToCachedConfig(key, values);

        if (next) {
            updateConfiguration(key, next);
        }
    };

    const handleConnectivityPropertiesChange = (values: ConnectivityProperties) =>
        handleSectionChange('connectivityProperties', values);
    const handleQcPropertiesChange = (values: QcProperties) => handleSectionChange('qcProperties', values);
    const handleCleaningPropertiesChange = (values: CleaningProperties) =>
        handleSectionChange('cleaningProperties', values);

    return {
        isLoading,
        config,
        handleValidityChange,
        handleConnectivityPropertiesChange,
        handleQcPropertiesChange,
        handleCleaningPropertiesChange,
    };
};
